import { CalendarEventType, DocumentType } from '@/features/calendar/types/calendar-dto';
import { OrderStatus, PaymentMethod } from '@/features/orders/types/orders-dto';

export enum BookingStatus {
    Pending,
    New,
    Ongoing,
    Cancelled,
}

export type SuperAdminDataLookupItemDto = {
    id: string;
    name: string;
};

export type SuperAdminDataLookupListResponseDto = {
    items: SuperAdminDataLookupItemDto[];
    totalCount: number;
};

export type SuperAdminSaleTransactionDto = {
    id: string;
    tenantId: string;
    financeDocumentType?: string | null;
    financeDocumentNumber?: string | null;
    financeDocumentDateTime?: string | null;
    financeDocumentId?: string | null;
    financeDocumentNumberNav?: string | null;
    folioId?: string | null;
    bookingId?: string | null;
    hotelId: string;
    accountingDate: string;
    serviceId: string;
    serviceName?: string | null;
    ownerId?: string | null;
    ownerName?: string | null;
    apartmentId?: string | null;
    apartmentName?: string | null;
    sales: number;
    salesWithoutVat: number;
    roomRevenue: number;
    roomRevenueWithoutVat: number;
    roomsRented: number;
    currency: string;
    currencyRate: number;
    roomRevenueCurrency: number;
};

export type SuperAdminSaleForecastTransactionDto = {
    id: string;
    tenantId: string;
    bookingId?: string | null;
    hotelId: string;
    accountingDate: string;
    serviceId: string;
    serviceName?: string | null;
    ownerId?: string | null;
    ownerName?: string | null;
    apartmentId?: string | null;
    apartmentName?: string | null;
    sales: number;
    salesWithoutVat: number;
    roomRevenue: number;
    roomRevenueWithoutVat: number;
    roomsRented: number;
    currency: string;
    currencyRate: number;
    roomRevenueCurrency: number;
};

export type SuperAdminOrderLineDto = {
    id: string;
    tenantId: string;
    orderId: string;
    orderItemId: string;
    orderItemName?: string | null;
    quantity: number;
    price: number;
    amount: number;
};

export type SuperAdminBookingListItemDto = {
    id: string;
    version: number;
    tenantId: string;
    tenantName?: string | null;
    externalId?: string | null;
    checkInDate: string;
    checkOutDate: string;
    hotelId: string;
    hotelName?: string | null;
    apartmentId?: string | null;
    apartmentName?: string | null;
    ownerId?: string | null;
    ownerName?: string | null;
    eventType: CalendarEventType;
    documentType: DocumentType;
    remarks?: string | null;
    numberOfRooms: number;
    totalAmount: number;
    isActive: boolean;
    hasAccommodation: boolean;
    bookingStatus: BookingStatus;
    guestFullName?: string | null;
    documentDate: string;
    documentNumber?: string | null;
    saleTransactions: SuperAdminSaleTransactionDto[];
    saleForecastTransactions: SuperAdminSaleForecastTransactionDto[];
};

export type UpdateSuperAdminBookingRequestDto = {
    hotelId: string;
    apartmentId?: string | null;
    checkInDate: string;
    checkOutDate: string;
    guestFullName?: string | null;
    remarks?: string | null;
    eventType: CalendarEventType;
    documentType: DocumentType;
    bookingStatus: BookingStatus;
    numberOfRooms: number;
    totalAmount: number;
    isActive: boolean;
    hasAccommodation: boolean;
    documentNumber?: string | null;
    documentDate: string;
    externalId?: string | null;
};

export type SuperAdminOrderListItemDto = {
    id: string;
    version: number;
    tenantId: string;
    tenantName?: string | null;
    externalId?: string | null;
    hotelId: string;
    hotelName?: string | null;
    authorId: string;
    authorName?: string | null;
    apartmentId: string;
    apartmentName?: string | null;
    ownerId?: string | null;
    ownerName?: string | null;
    totalAmount: number;
    status: OrderStatus;
    deliveryDate: string;
    paymentMethod: PaymentMethod;
    serviceId: string;
    serviceName?: string | null;
    documentDate: string;
    documentNumber?: string | null;
    deletionMark: boolean;
    syncStatus?: string | null;
    syncAction?: string | null;
    lines: SuperAdminOrderLineDto[];
};

export type SuperAdminBookingListResponseDto = {
    items: SuperAdminBookingListItemDto[];
    totalCount: number;
};

export type SuperAdminOrderListResponseDto = {
    items: SuperAdminOrderListItemDto[];
    totalCount: number;
};

export type SuperAdminDataLookupSelection = {
    id: string;
    name?: string;
};

export type SuperAdminDataListFilterState = {
    take: number;
    skip: number;
    startDate: Date | null;
    endDate: Date | null;
    tenantId: string | null;
    tenantName?: string | null;
    ownerId: string | null;
    ownerName?: string | null;
    apartmentId: string | null;
    apartmentName?: string | null;
    hotelId: string | null;
    hotelName?: string | null;
    documentNumber: string | null;
    orderBy: string | null;
    orderDirection: 'asc' | 'desc';
};

export enum OwnerStatus {
    Active,
    Inactive,
    Suspended,
}

export type SuperAdminOwnerPermissionsDto = {
    showCalendar: boolean;
    showFinances: boolean;
    createBookings: boolean;
    placeOrders: boolean;
    bookingDurationLimit: number;
    yearlyBookingLimit: number;
    loginToAccount: boolean;
};

export type SuperAdminOwnerApartmentDto = {
    id: string;
    name: string;
    externalId?: string | null;
    hotelName?: string | null;
    isVirtual: boolean;
    deletionMark: boolean;
};

export type SuperAdminOwnerListItemDto = {
    id: string;
    tenantId: string;
    tenantName?: string | null;
    externalId?: string | null;
    name: string;
    tin?: string | null;
    email?: string | null;
    phoneNumber?: string | null;
    language?: string | null;
    status: OwnerStatus;
    userId?: string | null;
    avatarUrl?: string | null;
    billingAddress?: string | null;
    personalId?: string | null;
    deletionMark: boolean;
};

export type SuperAdminOwnerDetailDto = SuperAdminOwnerListItemDto & {
    permissions?: SuperAdminOwnerPermissionsDto | null;
    apartments: SuperAdminOwnerApartmentDto[];
};

export type SuperAdminApartmentAmenityDto = {
    amenityId: string;
    amenityName?: string | null;
};

export type SuperAdminApartmentPhotoDto = {
    id: string;
    name: string;
    url: string;
};

export type SuperAdminApartmentListItemDto = {
    id: string;
    tenantId: string;
    tenantName?: string | null;
    externalId?: string | null;
    name: string;
    description?: string | null;
    hotelId: string;
    hotelName?: string | null;
    ownerId?: string | null;
    ownerName?: string | null;
    apartmentTypeId: string;
    apartmentTypeName?: string | null;
    cadastralCode?: string | null;
    squareness?: string | null;
    electricityId?: string | null;
    waterId?: string | null;
    gasId?: string | null;
    internetId?: string | null;
    isVirtual: boolean;
    deletionMark: boolean;
};

export type SuperAdminApartmentDetailDto = SuperAdminApartmentListItemDto & {
    amenities: SuperAdminApartmentAmenityDto[];
    photos: SuperAdminApartmentPhotoDto[];
};

export type SuperAdminOwnerListResponseDto = {
    items: SuperAdminOwnerListItemDto[];
    totalCount: number;
};

export type SuperAdminApartmentListResponseDto = {
    items: SuperAdminApartmentListItemDto[];
    totalCount: number;
};

export type SuperAdminRentChargeInfoDto = {
    id: string;
    tenantId: string;
    rentChargeId: string;
    hotelId: string;
    hotelName?: string | null;
    apartmentId: string;
    apartmentName?: string | null;
    ownerId: string;
    ownerName?: string | null;
    month: string;
    roomsRented: number;
    revenue: number;
    revenueVat: number;
    electricity: number;
    water: number;
    internet: number;
    comissions: number;
    incomeTax: number;
    total: number;
    adGas: number;
    adPropertyManagement: number;
    adAgentCommission: number;
    utilities: number;
    previousMonthUtilities: number;
    totalAfterPropertyManagement: number;
    startingBalance: number;
    paidToOwnerSum: number;
    totalCurrentMonth: number;
    currentBalance: number;
    grossRevenue: number;
    deduction: number;
    transferredFunds: number;
};

export type SuperAdminRentChargeListItemDto = {
    id: string;
    tenantId: string;
    tenantName?: string | null;
    externalId?: string | null;
    documentDate: string;
    documentNumber?: string | null;
    deletionMark: boolean;
};

export type SuperAdminRentChargeDetailDto = SuperAdminRentChargeListItemDto & {
    infos: SuperAdminRentChargeInfoDto[];
};

export type SuperAdminRentChargeListResponseDto = {
    items: SuperAdminRentChargeListItemDto[];
    totalCount: number;
};

export type SuperAdminCatalogFilterState = {
    take: number;
    skip: number;
    tenantId: string | null;
    ownerId: string | null;
    searchText: string | null;
    orderBy: string | null;
    orderDirection: 'asc' | 'desc';
};
